import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

// Service APIs
import createOperatorConnectService from 'API/Service/createOperatorConnectService';
import createDirectRoutingService from 'API/Service/createDirectRoutingService';
import createSbcService from 'API/Service/createSbcService';
import createGCCHService from 'API/Service/createGCCHService';

// Region API
import getAvailableRegionsFromACompany from '../API/Service/getAvailableRegionsFromACompany';

// Company API
import getCompany from 'API/Company/getCompany';
import getMsLoginLink from 'API/Company/getMsLoginLink';
import getGCCHLoginLink from 'API/Company/getGCCHLoginLink';

// Auth API
// import createMicrosoftAppAuth from 'API/AppAuth/createMicrosoftAppAuth';
import { createMicrosoftAppAuth, updateAppAuth } from 'API/AppAuth/';
import getAppAuthByCompanyId from 'API/AppAuth/getAppAuthByCompanyId';
import getAppAuthById from 'API/AppAuth/getAppAuthById';
import getTrunkTypesServiceCompany from 'API/Service/getTrunkTypesServiceCompany';
import getSRTRegionsForCompany from 'API/SRT/getSRTRegionsForCompany';
import getSRTTrunkTypesForCompany from 'API/SRT/getSRTTrunkTypesForCompany';
import createSRTService from 'API/SRT/createSRTService';
import { useToast } from '@chakra-ui/react';
import { useDashboardMenu } from 'context/DashboardMenuContext';
import getZoomLoginLink from 'API/Company/getZoomLoginLink';
import getEightByEightLoginLink from 'API/Company/getEightByEightLoginLink';
import getRingCentralLoginLink from 'API/Company/getRingCentralLoginLink';
import getDialPadLoginLink from 'API/Company/getDialPadLoginLink';
import getWebexLoginLink from 'API/Company/getWebexLoginLink';
import { useCreateService } from 'hooks/useCreateService';
import { useNavigateLocation } from 'hooks/useNavigateLocation/useNavigateLocation';
import resolveGrantAccessAction from 'hooks/resolveGrantAccessAction';
import { axios } from 'services/axios';
import path from '@/routes/constants';
import { PlatformEnums } from 'utils/platforms';
import {
    SIMPLE_REDIRECT_AUTH_PLATFORMS,
    getSimpleRedirectPlatformByServiceType,
    getSimpleRedirectPlatformByWizardServiceType,
    getSimpleRedirectPlatformByAuthServiceType,
    isGenericSimpleServiceProfileServiceType,
} from 'services/platformRegistry';

// Pairs each simple-redirect platform's registry `key` with its real
// getXLoginLink API call. Kept out of platformRegistry.js itself so that
// pure/presentational consumers (CardDetails, statusReducer, dashboard cards)
// don't transitively import services/axios.
const SIMPLE_REDIRECT_LOGIN_LINK_FNS = {
    Zoom: getZoomLoginLink,
    EightByEight: getEightByEightLoginLink,
    RingCentral: getRingCentralLoginLink,
    DialPad: getDialPadLoginLink,
    Webex: getWebexLoginLink,
};

/**
 * @param {object} [options]
 * @param {string} [options.dashboardPath] - (2635) where the wizard returns
 *   once a flow finishes, so the new dashboard comes back to itself
 */
export default function useServiceWizard({
    dashboardPath = '/dashboard-services',
} = {}) {
    const navigate = useNavigate();
    const toast = useToast();
    const queryClient = useQueryClient();
    const { handleNavigateLocation: navigateLocation } = useNavigateLocation();
    const wizardRef = useRef({});

    // Wizard Flow
    const [flow, setFlow] = useState('Start'); // Start | CreateService | SelectiveSyncUsers | SelectiveSyncUsersEdit | MsAuth

    const [selectedServiceProfile, setSelectedServiceProfile] = useState(null);
    const [serviceCreationResponse, setServiceCreationResponse] =
        useState(null);

    // EightByEight wizard data
    const [eightByEightCompanyDetails, setEightByEightCompanyDetails] =
        useState(null);

    // Selective Sync
    const [filterGroup, setFilterGroup] = useState(null);
    const [filterLocations, setFilterLocations] = useState(null);
    const [selectiveSyncEditData, setSelectiveSyncEditData] = useState(null);
    const [selectiveSyncToggle, setSelectiveSyncToggle] = useState(false);

    // State
    const [company, setCompany] = useState();
    const [companyAppAuth, setCompanyAppAuth] = useState([]);
    const [msServiceAuth, setMsServiceAuth] = useState({});

    const [isCreatingService, setIsCreatingService] = useState(false);
    const [isCreatingAuth, setIsCreatingAuth] = useState(false);

    // Errors
    const [hasCreateServiceError, setHasCreateServiceError] = useState(null);

    // Other
    const currentCompany = useSelector(
        (state) => state.navigationLists.currentCompany,
    );

    const { requiredScope: accountScope } = useSelector((state) => state.login);
    const { restError: errorMessage } = useSelector((state) => ({
        ...state.modal,
    }));

    const { setIsQueryDisabled } = useDashboardMenu();

    const simpleRedirectAppAuthTypeIDs = Object.values(
        SIMPLE_REDIRECT_AUTH_PLATFORMS,
    ).map((p) => p.appAuthTypeID);

    const {
        Microsoft: AppAuthService,
        Zoom: ZoomAppAuthService,
        EightByEight: EightByEightAppAuthService,
        RingCentral: RingCentralAppAuthService,
        DialPad: DialPadAppAuthService,
        WebexAppAuth: WebexAppAuthService,
    } = companyAppAuth?.length > 0 &&
    companyAppAuth.reduce((acc, item) => {
        if (
            (item?.appAuthTypeID === 'Microsoft' &&
                item?.platformID === PlatformEnums.TEAMS) ||
            simpleRedirectAppAuthTypeIDs.includes(item?.appAuthTypeID)
        ) {
            acc[item.appAuthTypeID] = item;
        }
        return acc;
    }, {});
    const GccHighAppAuthService =
        companyAppAuth?.length > 0
            ? companyAppAuth.find(
                  (item) =>
                      item?.appAuthTypeID === 'Microsoft' &&
                      item?.platformID ===
                          'b8369b29-823d-4b3d-a12f-4f708993d0a8',
              )
            : null;

    function reset() {
        setFlow('Start');
        setSelectedServiceProfile(null);
        setFilterGroup('');
        setFilterLocations(null);
        setSelectiveSyncToggle(null);
        setIsCreatingService(false);
        setIsCreatingAuth(false);
        setHasCreateServiceError(null);
        setSelectiveSyncEditData(null);
        setIsQueryDisabled(false);
    }

    function updateLocations(multiselectValues) {
        let items = [];
        multiselectValues.map((multiSelect) => {
            items.push(multiSelect.value);
        });
        setFilterLocations(items);
    }

    async function microsoftAuth() {
        const redirectUrl = await getMsLoginLink(currentCompany);
        window.location.href = redirectUrl.data;
    }

    // Zoom/8x8/RingCentral/DialPad/Webex share one shape: redirect to a login
    // link and nothing else. See services/platformRegistry.js.
    async function runSimpleRedirectAuth(platform) {
        const getLoginLink = SIMPLE_REDIRECT_LOGIN_LINK_FNS[platform.key];
        if (!getLoginLink) return undefined;
        const redirectUrl = await getLoginLink(currentCompany);
        window.location.href = redirectUrl;
    }

    async function zoomAuth() {
        return runSimpleRedirectAuth(SIMPLE_REDIRECT_AUTH_PLATFORMS.Zoom);
    }

    async function eightByEightAuth() {
        return runSimpleRedirectAuth(
            SIMPLE_REDIRECT_AUTH_PLATFORMS.EightByEight,
        );
    }

    async function ringCentralAuth() {
        return runSimpleRedirectAuth(
            SIMPLE_REDIRECT_AUTH_PLATFORMS.RingCentral,
        );
    }

    async function dialPadAuth() {
        return runSimpleRedirectAuth(SIMPLE_REDIRECT_AUTH_PLATFORMS.DialPad);
    }

    async function webexAuth() {
        return runSimpleRedirectAuth(SIMPLE_REDIRECT_AUTH_PLATFORMS.Webex);
    }

    // Generic entry point for any simple-redirect platform, keyed by its
    // service, wizard-selection, or auth serviceType constant — used by
    // dashboard/auth-card components instead of calling a named xAuth().
    async function grantAccessForServiceType(serviceType) {
        const platform =
            getSimpleRedirectPlatformByServiceType(serviceType) ??
            getSimpleRedirectPlatformByWizardServiceType(serviceType) ??
            getSimpleRedirectPlatformByAuthServiceType(serviceType);
        if (!platform) return undefined;
        return runSimpleRedirectAuth(platform);
    }

    async function gcchAuth() {
        const redirectUrl = await getGCCHLoginLink(currentCompany);
        window.location.href = redirectUrl.data;
    }

    async function createMicrosoftAuth(selectiveSync = false) {
        setIsCreatingAuth(true);
        await createMicrosoftAppAuth(
            {
                filterGroup,
                filterLocations,
                companyID: currentCompany,
                selectiveSync: selectiveSync || selectiveSyncToggle,
            },
            accountScope,
        )
            .then((response) => response)
            .catch((error) => {
                console.log(error);
            })
            .finally(() => setIsCreatingAuth(false));
    }

    async function createGCCHAuth(selectiveSync = false) {
        setIsCreatingAuth(true);
        await createMicrosoftAppAuth(
            {
                filterGroup,
                filterLocations,
                companyID: currentCompany,
                selectiveSync: selectiveSync || selectiveSyncToggle,
                platformId: PlatformEnums.GCC,
            },
            accountScope,
        )
            .then((response) => response)
            .catch((error) => {
                console.log(error);
            })
            .finally(() => setIsCreatingAuth(false));
    }

    /**
     * disables your selective sync
     */
    async function onSelectiveSyncDisable() {
        await updateAppAuth(
            {
                id: msServiceAuth.id,
                tenantID: msServiceAuth.tenantID,
                filterGroup: null,
                filterLocations: null,
                companyID: currentCompany,
                selectiveSync: false,
                userSync: msServiceAuth.userSync, //TODO: check
            },
            accountScope,
        )
            .then(() => {
                setFlow('Start');
                toast({
                    title: 'Selective Sync Updated',
                    description: 'Selective Sync has been updated.',
                    status: 'success',
                    duration: 3000,
                });
            })
            .catch((error) => {
                console.error(error);
                toast({
                    title: 'Error',
                    description:
                        errorMessage ||
                        'There was an error updating selective sync.',
                    status: 'error',
                    duration: 3000,
                });
            });
    }

    // This is called when clicking no to selective sync
    async function onSelectiveSyncDeny() {
        const { serviceType } = selectedServiceProfile;

        if (serviceType === 'DirectRouting') {
            await microsoftAuth();
        }
        if (serviceType === 'DirectRoutingGCCH') {
            await gcchAuth();
        } else if (serviceType === 'OperatorConnect') {
            setFlow('Start');
        }
    }

    // This is called when clicking next in the selective sync form
    async function onSelectiveSync() {
        const { serviceType } = selectedServiceProfile;
        if (serviceType === 'DirectRoutingGCCH') {
            await createGCCHAuth(true).then(async () => {
                await gcchAuth();
            });
            return;
        } else {
            await createMicrosoftAuth(true).then(async () => {
                await microsoftAuth();
            });
        }
    }

    // This is called when editing selective sync
    async function onSelectiveSyncEdit() {
        let items = [];
        filterLocations.map((item) => {
            items.push(item.value);
        });
        await updateAppAuth(
            {
                id: msServiceAuth.id,
                tenantID: msServiceAuth.tenantID,
                filterGroup,
                filterLocations: items,
                companyID: currentCompany,
                selectiveSync: true,
                userSync: true,
            },
            accountScope,
        )
            .then(() => {
                setFlow('Start');
                toast({
                    title: 'Selective Sync Updated',
                    description: 'Selective Sync has been updated.',
                    status: 'success',
                    duration: 3000,
                });
            })
            .catch((error) => {
                console.error(error);
                toast({
                    title: 'Error',
                    description:
                        errorMessage ||
                        'There was an error updating selective sync.',
                    status: 'error',
                    duration: 3000,
                });
            });
    }

    async function ocFlow(serviceResponse) {
        // Flags
        let syncForced;
        let authDisabled;
        let userSync = false;
        let graphAuth = false;
        let serviceAuth;

        // Set all sync policy manually if were calling this function directly
        // Otherwise, grab the values from the service create response

        if (!serviceResponse) {
            const company = await getCompany(currentCompany);
            const { authPolicy } = selectedServiceProfile;
            const { selectiveSyncPolicy } = company;
            authDisabled = authPolicy === 0;
            syncForced = selectiveSyncPolicy === 2;
        } else {
            authDisabled = serviceResponse.authPolicy === 0;
            syncForced = serviceResponse.company.selectiveSyncPolicy === 2;
        }

        // Get the auth status
        const companyAppAuths = await getAppAuthByCompanyId(currentCompany);
        const companyAppAuthsContainThisService = companyAppAuths.data.find(
            (appAuth) => {
                const matchesType = serviceResponse
                    ? serviceResponse.appAuthType === appAuth.appAuthTypeID
                    : appAuth.appAuthTypeID === 'Microsoft';
                if (appAuth.appAuthTypeID === 'Microsoft') {
                    return (
                        matchesType &&
                        appAuth.platformID === PlatformEnums.TEAMS
                    );
                }
                return matchesType;
            },
        );

        if (companyAppAuthsContainThisService?.id) {
            serviceAuth = await getAppAuthById(
                companyAppAuthsContainThisService.id,
            );
            if (serviceAuth.data?.userSync) {
                userSync = true;
            }
            if (serviceAuth.data?.graphAuth) {
                graphAuth = true;
            }
        }

        switch (
            resolveGrantAccessAction({
                graphAuth,
                userSync,
                authDisabled,
                syncForced,
                serviceAuth,
            })
        ) {
            case 'start':
                setFlow('Start');
                break;
            case 'selectiveSync':
                setFlow('SelectiveSyncUsers');
                break;
            default:
                await microsoftAuth();
        }
    }

    async function drFlow(serviceResponse) {
        // Flags
        let syncForced;
        let userSync,
            graphAuth = false;
        let serviceAuth;

        setSelectedServiceProfile(serviceResponse);
        // Set all sync policy manually if were calling this function directly
        // Otherwise, grab the values from the service create response
        if (!serviceResponse) {
            const company = await getCompany(currentCompany);
            const { selectiveSyncPolicy } = company;
            syncForced = selectiveSyncPolicy === 2;
        } else {
            syncForced = serviceResponse.company.selectiveSyncPolicy === 2;
        }

        // Get the auth status
        const companyAppAuths = await getAppAuthByCompanyId(currentCompany);

        const expectedMsPlatform =
            serviceResponse?.serviceType === 'DirectRoutingGCCH'
                ? PlatformEnums.GCC
                : PlatformEnums.TEAMS;

        const companyAppAuthsContainThisService = companyAppAuths.data.find(
            (appAuth) => {
                const matchesType = serviceResponse
                    ? serviceResponse.appAuthType === appAuth.appAuthTypeID
                    : appAuth.appAuthTypeID === 'Microsoft';
                if (appAuth.appAuthTypeID === 'Microsoft') {
                    return (
                        matchesType && appAuth.platformID === expectedMsPlatform
                    );
                }
                return matchesType;
            },
        );

        if (companyAppAuthsContainThisService?.id) {
            serviceAuth = await getAppAuthById(
                companyAppAuthsContainThisService.id,
            );
            if (serviceAuth.data?.userSync) {
                userSync = true;
            }
            if (serviceAuth.data?.graphAuth) {
                graphAuth = true;
            }
        }

        switch (
            resolveGrantAccessAction({
                graphAuth,
                userSync,
                syncForced,
                serviceAuth,
            })
        ) {
            case 'start':
                setFlow('Start');
                break;
            case 'selectiveSync':
                setFlow('SelectiveSyncUsers');
                break;
            default:
                if (serviceResponse?.serviceType === 'DirectRoutingGCCH') {
                    await gcchAuth();
                } else {
                    await microsoftAuth();
                }
        }
    }

    async function serviceFlow(serviceResponse, serviceType) {
        // Flags
        let authEnabled, authForced;
        let userSync;
        let serviceAuth;
        const setServiceAuth = () => grantAccessForServiceType(serviceType);
        if (serviceResponse) {
            authEnabled = serviceResponse.authPolicy === 1;
            authForced = serviceResponse.authPolicy === 2;
        }
        // Get the auth status
        const companyAppAuths = await getAppAuthByCompanyId(currentCompany);

        const companyAppAuthsContainThisService = companyAppAuths.data.find(
            (appAuth) => {
                if (!serviceResponse) {
                    return (
                        companyAppAuths.data.appAuthType ===
                        appAuth.appAuthTypeID
                    );
                }
                if (serviceResponse.appAuthType === appAuth.appAuthTypeID) {
                    return true;
                }
                // RingCentral/DialPad: service.appAuthType is "XAppAuth" but
                // companyAppAuth entries use appAuthTypeID "X" (the plain
                // serviceType) — see services/platformRegistry.js.
                if (
                    isGenericSimpleServiceProfileServiceType(
                        serviceResponse.serviceType,
                    ) &&
                    appAuth.appAuthTypeID === serviceResponse.serviceType
                ) {
                    return true;
                }
                return false;
            },
        );

        if (companyAppAuthsContainThisService?.id) {
            serviceAuth = await getAppAuthById(
                companyAppAuthsContainThisService.id,
            );
            if (serviceAuth.data?.userSync) {
                userSync = true;
            }
        }

        if (
            (authEnabled || authForced) &&
            serviceAuth?.data?.status === 2 && //Pending Access
            userSync
        ) {
            setFlow('Start');
        } else if (!serviceAuth) {
            try {
                await setServiceAuth();
            } catch {
                navigate(`/reload${dashboardPath}`);
            }
        } else {
            navigate(`/reload${dashboardPath}`);
        }
    }

    async function onServiceCreate(serviceResponse) {
        setServiceCreationResponse(serviceResponse);

        // 3. If SBC-family service, finish now
        if (
            serviceResponse.serviceType === 'SBCaaS' ||
            serviceResponse.serviceType === 'SRT' ||
            serviceResponse.serviceType === 'OperatorConnectConferencing'
        ) {
            setFlow('Start');
            setIsQueryDisabled(false);
        }

        // 4. Direct Routing
        else if (
            serviceResponse.serviceType === 'DirectRouting' ||
            serviceResponse.serviceType === 'DirectRoutingGCCH'
        ) {
            drFlow(serviceResponse);
        }

        // 5. Operator Connect
        else if (serviceResponse.serviceType === 'OperatorConnect') {
            ocFlow(serviceResponse);
        }

        // 9. Operator Platform
        else if (serviceResponse.serviceType === 'OperatorPlatform') {
            window.location.reload();
        }
        // 6-9. Zoom/EightByEight/RingCentral/DialPad/Webex all just redirect to
        // a login link — reset to dashboard after magic service creation; the
        // auth link flow itself is wired up but, for RingCentral/DialPad, not
        // yet active pending BE. See services/platformRegistry.js.
        else {
            const simplePlatform =
                getSimpleRedirectPlatformByServiceType(
                    serviceResponse.serviceType,
                ) ??
                getSimpleRedirectPlatformByWizardServiceType(
                    serviceResponse.serviceType,
                );
            if (simplePlatform) {
                serviceFlow(serviceResponse, simplePlatform.wizardServiceType);
            }
        }
    }

    const { createNewService } = useCreateService(
        currentCompany,
        setIsCreatingService,
        setHasCreateServiceError,
        onServiceCreate,
    );

    // Called from the EightByEightCreate wizard after collecting company details
    function createEightByEightService(serviceProfile) {
        createNewService({
            serviceProfile,
            serviceType: 'EightByEight',
            serviceName: 'Eight By Eight',
        });
    }

    async function createService(serviceProfile) {
        setSelectedServiceProfile(serviceProfile);
        setHasCreateServiceError(null);
        switch (serviceProfile.serviceType) {
            case 'DirectRouting':
                setIsCreatingService(true);
                createDirectRoutingService(currentCompany).then((response) => {
                    if (response?.response?.data?.message) {
                        setHasCreateServiceError(
                            response.response.data.message,
                        );
                    } else {
                        onServiceCreate(response.data);
                    }
                });
                break;
            case 'DirectRouting-':
                onServiceCreate({
                    serviceType: 'DirectRouting',
                    authPolicy: 2,
                    company: { selectiveSyncPolicy: 2 },
                    appAuthType: 'Microsoft',
                });
                setServiceCreationResponse({
                    serviceType: 'DirectRouting',
                    authPolicy: 2,
                    company: { selectiveSyncPolicy: 2 },
                    appAuthType: 'Microsoft',
                });
                break;
            case 'DirectRoutingGCCH':
                setIsCreatingService(true);
                createGCCHService(currentCompany).then((response) => {
                    if (response?.response?.data?.message) {
                        setHasCreateServiceError(
                            response.response.data.message,
                        );
                    } else {
                        onServiceCreate(response.data);
                    }
                });
                break;
            case 'DirectRoutingGCCH-':
                onServiceCreate({
                    serviceType: 'DirectRoutingGCCH',
                    authPolicy: 2,
                    company: { selectiveSyncPolicy: 2 },
                    appAuthType: 'DirectRoutingGCCHAppAuth',
                });
                setServiceCreationResponse({
                    serviceType: 'DirectRoutingGCCH',
                    authPolicy: 2,
                    company: { selectiveSyncPolicy: 2 },
                    appAuthType: 'DirectRoutingGCCHAppAuth',
                });
                break;
            case 'OperatorConnect': {
                // Early validation BEFORE setting isCreatingService
                const selectiveSyncPolicy = company?.selectiveSyncPolicy ?? 0;
                const { authPolicy } = serviceProfile;

                if (
                    authPolicy === 0 &&
                    (selectiveSyncPolicy === 1 || selectiveSyncPolicy === 2)
                ) {
                    setHasCreateServiceError(
                        'Selective Sync is enabled for this Company, however the Service Profile for Operator Connect does not allow Auth.',
                    );
                    return;
                }

                setIsCreatingService(true);
                createOperatorConnectService(currentCompany).then(
                    (response) => {
                        if (response?.response?.data?.message) {
                            setHasCreateServiceError(
                                response.response.data.message,
                            );
                        } else {
                            onServiceCreate(response.data);
                        }
                    },
                );
                break;
            }
            case 'OperatorConnect--':
                onServiceCreate({
                    authPolicy: 1,
                    company: { selectiveSyncPolicy: 1 },
                    appAuthType: 'Microsoft',
                    serviceType: 'OperatorConnect',
                });
                setServiceCreationResponse({
                    authPolicy: 1,
                    company: { selectiveSyncPolicy: 1 },
                    appAuthType: 'Microsoft',
                    serviceType: 'OperatorConnect',
                });
                break;
            case 'SBCaaS':
                setIsCreatingService(true);
                Promise.all([
                    getAvailableRegionsFromACompany(currentCompany),
                    getTrunkTypesServiceCompany(currentCompany),
                ]).then(([regionsResult, trunkTypesResult]) => {
                    createSbcService(
                        currentCompany,
                        regionsResult.data,
                        trunkTypesResult.data,
                    ).then((response) => {
                        if (response?.response?.data.message) {
                            setHasCreateServiceError(
                                response.response.data.message,
                            );
                        } else {
                            onServiceCreate(response.data);
                        }
                    });
                });

                break;
            case 'SRT':
                setIsCreatingService(true);
                Promise.all([
                    getSRTRegionsForCompany(currentCompany),
                    getSRTTrunkTypesForCompany(currentCompany),
                ]).then(([regionsResult, trunkTypesResult]) => {
                    createSRTService(
                        currentCompany,
                        regionsResult.data,
                        trunkTypesResult.data,
                    ).then((response) => {
                        if (response?.response?.data?.message) {
                            setHasCreateServiceError(
                                response.response.data.message,
                            );
                        } else {
                            onServiceCreate(response.data);
                        }
                    });
                });

                break;
            case 'OperatorConnectConferencing':
                createNewService({
                    serviceProfile,
                    serviceType: 'OperatorConnectConferencing',
                    serviceName: 'Operator Connect Conferencing',
                });
                break;
            case 'ZoomPEX':
            case 'Webex':
            case 'EightByEight':
            case 'RingCentral':
            case 'DialPad': {
                // All five are the "redirect to a login link" shape — see
                // services/platformRegistry.js.
                const simplePlatform =
                    getSimpleRedirectPlatformByWizardServiceType(
                        serviceProfile.serviceType,
                    );
                createNewService({
                    serviceProfile,
                    serviceType: simplePlatform.serviceType,
                    serviceName: simplePlatform.createServiceName,
                });
                break;
            }

            case 'TeamsPhoneMobile': {
                setIsCreatingService(true);
                const { NUMBERS_TPM } = path;
                axios
                    .post(`/Service/TeamsPhoneMobile/Magic/${currentCompany}`)
                    .then((res) => {
                        navigateLocation(
                            `${NUMBERS_TPM}/${res.data.id}/activation`,
                        );
                        queryClient.invalidateQueries([
                            'services',
                            `company: ${currentCompany}`,
                        ]);
                        queryClient.invalidateQueries('getAssociatedAccounts');
                    })
                    .catch((e) => {
                        const message =
                            e?.response?.data?.message ||
                            'Error creating Teams Phone Mobile, Please Try Again.';
                        setHasCreateServiceError(message);
                    });
                break;
            }

            case 'OperatorPlatform': {
                createNewService({
                    serviceProfile,
                    serviceType: 'OperatorPlatform',
                    serviceName: 'Operator Platform',
                });
                break;
            }
        }
    }

    wizardRef.current = {
        flow,
        setFlow,
        createService,
        onSelectiveSync,
        onSelectiveSyncEdit,
        onSelectiveSyncDeny,
        setIsCreatingService,

        // Selective Sync
        filterGroup,
        setFilterGroup,
        filterLocations,
        setFilterLocations,
        updateLocations,
        selectiveSyncToggle,
        setSelectiveSyncToggle,

        // Service profile
        setSelectedServiceProfile,
        selectedServiceProfile,

        // Server response after creating a service
        serviceCreationResponse,

        // Auth check
        microsoftAuth,
        createMicrosoftAuth,
        gcchAuth,
        createGCCHAuth,

        // Auth Service
        AppAuthService,
        ZoomAppAuthService,
        EightByEightAppAuthService,
        RingCentralAppAuthService,
        DialPadAppAuthService,
        WebexAppAuthService,
        GccHighAppAuthService,

        // Zoom
        zoomAuth,

        // EightByEight
        eightByEightAuth,

        // RingCentral
        ringCentralAuth,

        // DialPad
        dialPadAuth,

        // Webex
        webexAuth,

        // Generic entry point for any simple-redirect platform (see
        // services/platformRegistry.js) — prefer this in new dashboard/auth-card
        // code over calling a named xAuth() directly.
        grantAccessForServiceType,

        // State
        isCreatingService,
        isCreatingAuth,
        hasCreateServiceError,
        reset,
        msServiceAuth,
        setMsServiceAuth,
        selectiveSyncEditData,
        setSelectiveSyncEditData,
        companyAppAuth,
        setCompanyAppAuth,
        drFlow,
        ocFlow,
        company,
        setCompany,
        onSelectiveSyncDisable,
        setHasCreateServiceError,
        createEightByEightService,
        eightByEightCompanyDetails,
        setEightByEightCompanyDetails,
    };

    return wizardRef.current;
}
